import { applicability, type PropertyFacts, type SkillPolicy } from './registry';

/**
 * The property's local time zone: the one zone the skills that apply to it
 * declare. Null when none does, or when two applicable skills disagree; then
 * the page shows times in the app's zone and the derivation counts days in UTC.
 */
export function propertyTimeZone(
  skills: ReadonlyArray<{ policy: SkillPolicy }>,
  facts: PropertyFacts,
): string | null {
  const zones = new Set(
    skills
      .filter(
        ({ policy }) => policy.timeZone && applicability(policy, facts).status === 'applicable',
      )
      // One zone under two names (an alias and its canonical name) is one zone.
      .map(({ policy }) => canonicalZone(policy.timeZone!)),
  );
  return zones.size === 1 ? [...zones][0]! : null;
}

function canonicalZone(timeZone: string) {
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone }).resolvedOptions().timeZone;
  } catch {
    return timeZone;
  }
}

/**
 * The calendar day an instant falls on in a time zone, as YYYY-MM-DD; UTC when
 * none, or when this runtime does not know the zone. Null for an invalid
 * instant, which callers treat as unknown.
 */
export function localDay(value: Date | string, timeZone?: string | null) {
  const date = typeof value === 'string' ? new Date(value) : value;
  if (!Number.isFinite(date.getTime())) return null;
  let format: Intl.DateTimeFormat;
  try {
    format = dayFormat(timeZone || 'UTC');
  } catch {
    format = dayFormat('UTC');
  }
  const parts = Object.fromEntries(
    format.formatToParts(date).map((part) => [part.type, part.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

const dayFormat = (timeZone: string) =>
  new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
